import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { Table, TableCellProps } from '@unistocks/components/table/table';
import { getValueCells } from '@unistocks/features/dashboard/components/calculated-value/value-cells';
import { ValueListItem } from '@unistocks/features/dashboard/components/value-list-item/value-list-item';
import { CalculatedValueResultDto, ValueType } from '@unistocks/proxy/axios-client';

export type ValueListRowProps = {
  calculatedValue?: CalculatedValueResultDto;
  label?: string;
  isLoading?: boolean;
  isError?: boolean;
  isStale?: boolean;
  valueCellWidth?: TableCellProps['cellWidth'];
};

export const ValueListRow: React.FC<ValueListRowProps> = ({
  calculatedValue,
  label,
  isLoading,
  isError,
  isStale,
  valueCellWidth = '$8'
}) => {
  const { cells, isMultiValue } = getValueCells(calculatedValue, { isStale });
  const totalCell = cells[ValueType.TotalValue];
  const nominalCell = cells[ValueType.NominalChange];
  const percentageCell = cells[ValueType.PercentageChange];
  const primaryCell = cells[calculatedValue?.valueType ?? ValueType.TotalValue];

  const hasColumnContent = !!percentageCell || !!nominalCell || !isMultiValue;
  const hasTwoRows = !!percentageCell && !!nominalCell;

  return (
    <>
      <Table.Cell alignCells={{ x: 'start' }} gap={'$2'} px={0}>
        <ValueListItem.Image size={'$1.5'} calculatedValue={calculatedValue} />
        <ValueListItem.Label>{calculatedValue?.displayName ?? label}</ValueListItem.Label>
      </Table.Cell>

      {isLoading ? (
        <Table.Cell alignCells={{ x: 'end', y: 'center' }} px={0}>
          <Spinner color={'$bodyFont'} size="small" scale={0.75} />
        </Table.Cell>
      ) : isError ? (
        <Table.Cell alignCells={{ x: 'end', y: 'center' }} px={0}>
          <ValueListItem.Value status={'neutral'} textAlign="right">
            {'-'}
          </ValueListItem.Value>
        </Table.Cell>
      ) : (
        <>
          {totalCell ? (
            <Table.Cell
              cellWidth={valueCellWidth}
              alignCells={{ x: 'end', y: hasTwoRows ? 'start' : 'center' }}
              flexGrow={0}
              px={0}
              mr={'$2'}
            >
              <ValueListItem.Value status={totalCell.status} textAlign="right">
                {totalCell.formattedValue}
              </ValueListItem.Value>
            </Table.Cell>
          ) : null}

          {hasColumnContent ? (
            <Table.Cell
              cellWidth={valueCellWidth}
              alignCells={{ x: 'end', y: 'center' }}
              fd={hasTwoRows ? 'column' : 'row'}
              flexGrow={0}
              px={0}
            >
              {percentageCell ? (
                <ValueListItem.Value status={percentageCell.status} textAlign="right">
                  {percentageCell.formattedValue}
                </ValueListItem.Value>
              ) : null}
              {nominalCell ? (
                <ValueListItem.Value status={nominalCell.status} textAlign="right">
                  {nominalCell.formattedValue}
                </ValueListItem.Value>
              ) : null}

              {!isMultiValue && primaryCell ? (
                <ValueListItem.Value status={primaryCell.status} textAlign="right">
                  {primaryCell.formattedValue}
                </ValueListItem.Value>
              ) : null}
            </Table.Cell>
          ) : null}
        </>
      )}
    </>
  );
};

ValueListRow.displayName = 'ValueListRow';